import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { CompanyInterface } from "../../common/interfaces";
import { TypeSaleType, TypeSaleTypeLabel } from "../../components/enum/saleEnums";
import api from "../../services/api";
import {
  Badge,
  ClientBlock,
  DashedDivider,
  DueDateNotice,
  FiscalNote,
  Footer,
  FooterNote,
  GrandTotalBox,
  HardDivider,
  ItemDiscountTag,
  ItemName,
  ItemsTable,
  MetaGrid,
  QrCodeWrap,
  ReceiptWrapper,
  StoreLine,
  StoreName,
  ThanksLine,
  TotalsBlock,
  TotalsRow
} from "./Receipt.styled";

interface ReceiptSaleItem {
  product_name: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  discount?: number;
}

interface ReceiptProps {
  saleItems: ReceiptSaleItem[];
  total: number;
  saleNumber?: number;
  createdAt?: string | Date;
  cashierName?: string;
  saleType?: TypeSaleType;
  clientName?: string;
  clientDocument?: string;
  dueDate?: string | Date;
  onAfterPrint: () => void;
}

// due_date chega como campo DATE (sem hora). Montamos a data pelos
// componentes em vez de deixar o navegador interpretar o ISO como UTC —
// senão o cupom mostra o dia anterior em fusos negativos (ex.: Manaus).
const formatDueDate = (value: string | Date): string => {
  const iso = typeof value === "string" ? value : value.toISOString();
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return "—";
  return new Date(year, month - 1, day).toLocaleDateString("pt-BR");
};

const fmt = (value: number): string =>
  // toLocaleString insere um espaço (na verdade um  ) entre "R$" e o
  // valor — no monoespaçado do cupom térmico isso abre um vão largo demais.
  value
    .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    .replace(/\s/g, "");

const Receipt: React.FC<ReceiptProps> = ({
  saleItems,
  total,
  saleNumber,
  createdAt,
  cashierName,
  saleType,
  clientName,
  clientDocument,
  dueDate,
  onAfterPrint
}) => {
  const [company, setCompany] = useState<CompanyInterface | null>(null);

  // Busca os dados da empresa para o cabeçalho do cupom. Se falhar, ainda
  // assim seguimos para a impressão com um cabeçalho mínimo — travar aqui
  // deixaria a tela de vendas presa aguardando o cupom para sempre.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await api.get<CompanyInterface>(
          "/company/my-company"
        );
        if (!cancelled) setCompany(response.data);
      } catch (error) {
        console.error("Erro ao buscar empresa para o cupom:", error);
        if (!cancelled) setCompany({} as CompanyInterface);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Imprime a partir de um IFRAME isolado contendo somente o cupom.
  //
  // Por que não window.print() direto? Porque aí a página inteira entra no
  // cálculo de impressão: mesmo com o resto escondido via CSS, qualquer
  // conteúdo da tela de origem que não reflua para os 80mm do papel faz o
  // Chrome aplicar "shrink-to-fit" e encolher o cupom junto. Foi por isso
  // que o mesmo cupom saía em tamanho cheio na Frente de Caixa mas menor
  // em Vendas Realizadas. Num iframe que só contém o cupom, não existe
  // mais nada para influenciar a escala — a saída é idêntica em qualquer
  // tela que dispare a impressão.
  useEffect(() => {
    if (!company) return;

    const timer = setTimeout(() => {
      const receiptEl = document.querySelector(".receipt-wrapper");
      if (!receiptEl) {
        onAfterPrint();
        return;
      }

      const iframe = document.createElement("iframe");
      iframe.setAttribute("aria-hidden", "true");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) {
        iframe.remove();
        onAfterPrint();
        return;
      }

      // Serializa TODO o CSS da página via CSSOM (styled-components pode
      // injetar regras com insertRule, que não aparecem no textContent das
      // tags <style> — ler os cssRules cobre os dois casos).
      let cssText = "";
      for (const sheet of Array.from(document.styleSheets)) {
        try {
          for (const rule of Array.from(sheet.cssRules)) {
            cssText += rule.cssText + "\n";
          }
        } catch {
          // stylesheets cross-origin não são legíveis — nenhuma delas
          // estiliza o cupom, então podem ser ignoradas.
        }
      }

      doc.open();
      doc.write("<!doctype html><html><head><meta charset='utf-8'></head><body></body></html>");
      doc.close();

      const baseStyle = doc.createElement("style");
      baseStyle.textContent = cssText;
      doc.head.appendChild(baseStyle);

      // Overrides do contexto de impressão do cupom — por último para
      // vencer o `.receipt-wrapper { display: none }` da tela.
      const pageStyle = doc.createElement("style");
      pageStyle.textContent = `
        @page { size: 80mm auto; margin: 0; }
        html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
        .receipt-wrapper {
          display: block !important;
          width: 80mm !important;
          margin: 0 !important;
          box-sizing: border-box !important;
        }
      `;
      doc.head.appendChild(pageStyle);

      doc.body.appendChild(doc.importNode(receiptEl, true));

      // onAfterPrint dispara tanto se o usuário imprimir quanto se cancelar
      // o diálogo — nos dois casos a venda já foi concluída no backend,
      // então o carrinho deve ser limpo de qualquer forma.
      const cleanup = () => {
        setTimeout(() => {
          iframe.remove();
          onAfterPrint();
        }, 50);
      };
      win.onafterprint = cleanup;

      win.focus();
      win.print();
    }, 80);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  if (!company) return null;

  const grossTotal = saleItems.reduce(
    (sum, item) => sum + item.unit_price * item.quantity,
    0
  );
  const discountTotal = grossTotal - total;
  const hasDiscount = discountTotal > 0.005;

  const saleDate = createdAt ? new Date(createdAt) : new Date();

  // Texto simples com os dados da venda — não é um QR de NFC-e (esse cupom
  // não é fiscal), só um jeito rápido de guardar/conferir a venda depois.
  const qrData = [
    company.company_name || "Comprovante de Venda",
    company.company_cnpj ? `CNPJ: ${company.company_cnpj}` : null,
    saleNumber != null ? `Venda: #${saleNumber}` : null,
    `${saleDate.toLocaleDateString("pt-BR")} ${saleDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`,
    `Total: ${fmt(total)}`,
    cashierName ? `Operador: ${cashierName}` : null
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <ReceiptWrapper className="receipt-wrapper">
      <StoreName>{company.company_name || "Minha Empresa"}</StoreName>
      {company.company_cnpj && (
        <StoreLine>CNPJ: {company.company_cnpj}</StoreLine>
      )}
      {company.address?.address_street && (
        <StoreLine>
          {company.address.address_street}
          {company.address.address_number
            ? `, ${company.address.address_number}`
            : ""}
          {company.address.address_neighborhood
            ? ` - ${company.address.address_neighborhood}`
            : ""}
        </StoreLine>
      )}
      {company.phone?.phone_number && (
        <StoreLine>Tel: {company.phone.phone_number}</StoreLine>
      )}
      {company.company_email && (
        <StoreLine>{company.company_email}</StoreLine>
      )}

      {(clientName || clientDocument) && (
        <ClientBlock>
          {clientName ? `Cliente: ${clientName}` : "Cliente avulso"}
          {clientDocument ? ` — CPF/CNPJ: ${clientDocument}` : ""}
        </ClientBlock>
      )}

      <HardDivider />

      <Badge>Comprovante de Venda</Badge>

      <MetaGrid>
        {saleNumber != null && (
          <span>
            Venda: <b>#{saleNumber}</b>
          </span>
        )}
        <span>
          Data: <b>{saleDate.toLocaleDateString("pt-BR")}</b>
        </span>
        <span>
          Hora: <b>{saleDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</b>
        </span>
        {cashierName && (
          <span>
            Operador: <b>{cashierName}</b>
          </span>
        )}
        {saleType && (
          <span>
            Pagamento: <b>{TypeSaleTypeLabel[saleType]}</b>
          </span>
        )}
      </MetaGrid>

      <DashedDivider />

      <ItemsTable>
        <thead>
          <tr>
            <th>Descrição</th>
            <th>Qtd</th>
            <th>Unit.</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {saleItems.map((item, idx) => (
            <tr key={idx}>
              <td>
                <ItemName>{item.product_name}</ItemName>
                {!!item.discount && item.discount > 0 && (
                  <ItemDiscountTag>-{item.discount}% desc.</ItemDiscountTag>
                )}
              </td>
              <td>{item.quantity}</td>
              <td>{fmt(item.unit_price)}</td>
              <td>{fmt(item.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </ItemsTable>

      <DashedDivider />

      <TotalsBlock>
        {hasDiscount && (
          <>
            <TotalsRow>
              <span>Subtotal</span>
              <span>{fmt(grossTotal)}</span>
            </TotalsRow>
            <TotalsRow>
              <span>Descontos</span>
              <span>-{fmt(discountTotal)}</span>
            </TotalsRow>
          </>
        )}

        <GrandTotalBox>
          <span>Total</span>
          <span>{fmt(total)}</span>
        </GrandTotalBox>
      </TotalsBlock>

      {saleType === TypeSaleType.DEFERRED && dueDate && (
        <DueDateNotice>
          Pagamento previsto para: {formatDueDate(dueDate)}
        </DueDateNotice>
      )}

      <Footer>
        <ThanksLine>Obrigado pela preferência!</ThanksLine>
        <FooterNote>Volte sempre.</FooterNote>
      </Footer>

      <QrCodeWrap>
        <QRCodeSVG value={qrData} size={92} level="M" marginSize={0} />
      </QrCodeWrap>

      <DashedDivider />

      <FiscalNote>Este documento não possui valor fiscal</FiscalNote>
    </ReceiptWrapper>
  );
};

export default Receipt;
